import { Notification } from '@opallabs/wormhole/types'
import { timeBucketizedList } from './timeBucketizedList'
import { NotificationWrapperProps } from '.'

type NotificationMap = (arg: Notification[]) => Notification[]

const addHeaders: NotificationMap = (notifications) => {
  if (notifications.length === 0) {
    return notifications
  }
  const buckets = timeBucketizedList<Notification>({
    data: notifications,
    getValue: (notification: Notification) => notification.timestamp,
  })

  return buckets.reduce<Notification[]>((accum, current) => {
    const headerItem = current.items[0]

    if (typeof current.bucket.title === 'function') {
      headerItem.heading = current.bucket.title(current.items[0].timestamp)
    } else {
      headerItem.heading = current.bucket.title
    }

    if (typeof current.bucket.subTitle === 'function') {
      headerItem.subHeading = current.bucket.subTitle(
        current.items[0].timestamp
      )
    } else {
      headerItem.subHeading = current.bucket.subTitle
    }

    // Overwrite the `timeFormat` that's baked into `Notification`s if the
    // `current` bucket has a custom `timeFormat`. (See the functions in
    // `timeBucketedList.ts` that return `BucketMeta`.)
    let items = current.items
    if (current.bucket.timeFormat) {
      items = items.map((item) => ({
        ...item,
        // @note I had to add the fallback to satisfy TypeScript, even though
        //   I'd expect the `if` statement to narrow
        //   `current.bucket.timeFormat` from `string | undefined` to just
        //   `string`.
        timeFormat: current.bucket.timeFormat || item.timeFormat,
      }))
    }

    return [...accum, ...items]
  }, [])
}

type HandlerAdder = (arg: {
  notifications: Notification[]
  clickHandler?: (arg: Notification) => void
  removeHandler?: (id: string) => void
}) => NotificationWrapperProps[]

const addHandlers: HandlerAdder = ({
  notifications,
  clickHandler,
  removeHandler,
}) =>
  notifications.map((notification) => ({
    ...notification,
    onItemClick: (): void => {
      if (clickHandler !== undefined) clickHandler(notification)
    },
    onRemove: removeHandler
      ? (id: string): void => {
          removeHandler(id)
        }
      : undefined,
  }))

type NotificationToEntityMapper = (arg: {
  notifications: Notification[]
  clickHandler?: (arg: Notification) => void
  removeHandler?: (id: string) => void
}) => NotificationWrapperProps[]

export const mapNotificationsToEntities: NotificationToEntityMapper = ({
  notifications,
  clickHandler,
  removeHandler,
}) => {
  const withHeaders = addHeaders(notifications)

  const withHandlers = addHandlers({
    notifications: withHeaders,
    clickHandler,
    removeHandler,
  })

  return withHandlers
}
